"use client";

import {
  Container,
  Title,
  Text,
  Stack,
  Box,
  Card,
  Group,
  SimpleGrid,
  Paper,
  Button,
  Flex,
  ThemeIcon,
  List,
  ColorSwatch,
} from "@mantine/core";
import {
  IconCheck,
  IconX,
  IconArrowRight,
  IconCamera,
  IconPalette,
  IconEye,
  IconSparkles,
  IconAlertCircle,
} from "@tabler/icons-react";
import Link from "next/link";

export default function DoesAIColorAnalysisWorkContent() {
  // Visual demo: warm vs cool undertone colors
  const warmUndertoneColors = [
    { hex: "#FFBF00", name: "Golden Yellow" },
    { hex: "#FF6F61", name: "Coral" },
    { hex: "#E2725B", name: "Terracotta" },
    { hex: "#D2691E", name: "Warm Brown" },
  ];

  const coolUndertoneColors = [
    { hex: "#E0115F", name: "Ruby" },
    { hex: "#4169E1", name: "Royal Blue" },
    { hex: "#8B008B", name: "Deep Magenta" },
    { hex: "#2F4F4F", name: "Cool Gray" },
  ];

  // Sample result colors to show what you get
  const samplePaletteColors = [
    "#FBCEB1", "#F88379", "#C4E1C1", "#B3E3E2",
    "#FFE28A", "#FADADD", "#D6C9FF", "#87CEEB"
  ];
  const whatAICanDo = [
    {
      title: "Detect Skin Undertones",
      description:
        "AI analyzes pixel data to identify warm (yellow/golden), cool (pink/blue), or neutral undertones in your skin.",
      icon: IconPalette,
    },
    {
      title: "Measure Contrast Levels",
      description:
        "Calculates the difference between your skin, hair, and eye colors to determine if you're high, medium, or low contrast.",
      icon: IconEye,
    },
    {
      title: "Identify Color Depth",
      description:
        "Determines whether your overall coloring is light, medium, or deep based on your features.",
      icon: IconCamera,
    },
    {
      title: "Match to Seasonal Palettes",
      description:
        "Uses undertone + contrast + depth data to place you in one of the 12 seasonal color categories.",
      icon: IconSparkles,
    },
  ];

  const limitations = [
    "Photo lighting affects results — natural light is essential",
    "Heavy makeup can mask true undertones",
    "Some people fall between seasons (AI will note this uncertainty)",
    "Cannot account for personal style preferences",
  ];

  const advantages = [
    "No subjective bias from a consultant's personal color preferences",
    "Consistent, repeatable analysis based on measurable data",
    "Instant results without scheduling appointments",
    "Fraction of the cost of in-person consultations",
  ];

  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        {/* Hero Section */}
        <Box ta="center">
          <Title order={1} size="h1" mb="md">
            Does AI Color Analysis Actually Work?
          </Title>
          <Text size="lg" c="dimmed" maw={650} mx="auto" mb="xl">
            Yes — AI can accurately detect undertones, contrast, and color depth from a photo.
            Here's exactly what the technology does, and where it has limitations.
          </Text>
        </Box>

        {/* Direct Answer */}
        <Card withBorder p="xl" radius="md" bg="green.0">
          <Group align="flex-start" gap="md">
            <ThemeIcon size={50} radius="xl" color="green" variant="light">
              <IconCheck size={28} />
            </ThemeIcon>
            <Box>
              <Title order={2} size="h3" mb="sm">
                The Short Answer: Yes, It Works
              </Title>
              <Text size="md">
                AI color analysis uses computer vision to measure the actual color values in your skin,
                hair, and eyes. It's analyzing real data — not guessing. The same principles professional
                colorists use (undertone, contrast, depth) are what the AI measures, just with pixel-level precision.
              </Text>
            </Box>
          </Group>
        </Card>

        {/* What AI Can Do */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="lg" ta="center">
            What AI Color Analysis Actually Does
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            {whatAICanDo.map((item, index) => (
              <Paper key={index} withBorder p="lg" radius="md">
                <Group align="flex-start" gap="md">
                  <ThemeIcon color="violet" variant="light" size="lg">
                    <item.icon size={20} />
                  </ThemeIcon>
                  <Box style={{ flex: 1 }}>
                    <Text fw={600} mb="xs">
                      {item.title}
                    </Text>
                    <Text size="sm" c="dimmed">
                      {item.description}
                    </Text>
                  </Box>
                </Group>
              </Paper>
            ))}
          </SimpleGrid>
        </Card>

        {/* Visual Undertone Demo */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="lg" ta="center">
            What Undertone Detection Looks Like
          </Title>
          <Text size="md" ta="center" c="dimmed" mb="lg" maw={600} mx="auto">
            AI detects whether your skin has warm (yellow/golden) or cool (pink/blue) undertones.
            This determines which color families will flatter you most.
          </Text>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <Paper withBorder p="lg" radius="md" bg="orange.0">
              <Text fw={600} ta="center" mb="md">Warm Undertone Colors</Text>
              <Text size="sm" c="dimmed" ta="center" mb="md">
                If you have golden/peachy skin, these colors will make you glow
              </Text>
              <Group justify="center" gap="xs">
                {warmUndertoneColors.map((color, i) => (
                  <Box key={i} ta="center">
                    <ColorSwatch color={color.hex} size={44} radius="md" />
                    <Text size="xs" c="dimmed" mt={4}>{color.name}</Text>
                  </Box>
                ))}
              </Group>
            </Paper>
            <Paper withBorder p="lg" radius="md" bg="blue.0">
              <Text fw={600} ta="center" mb="md">Cool Undertone Colors</Text>
              <Text size="sm" c="dimmed" ta="center" mb="md">
                If you have pink/rosy skin, these colors will complement you
              </Text>
              <Group justify="center" gap="xs">
                {coolUndertoneColors.map((color, i) => (
                  <Box key={i} ta="center">
                    <ColorSwatch color={color.hex} size={44} radius="md" />
                    <Text size="xs" c="dimmed" mt={4}>{color.name}</Text>
                  </Box>
                ))}
              </Group>
            </Paper>
          </SimpleGrid>
          <Paper withBorder p="md" radius="md" mt="lg" bg="gray.0">
            <Text size="sm" c="dimmed" ta="center">
              Wearing the wrong undertone colors can make you look washed out, tired, or sallow —
              even if the color itself is beautiful. AI removes the guesswork.
            </Text>
          </Paper>
        </Card>

        {/* How It Works */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="lg" ta="center">
            How the Technology Works
          </Title>
          <Stack gap="md">
            <Paper withBorder p="lg" radius="md">
              <Title order={3} size="h4" mb="sm">
                1. Image Processing
              </Title>
              <Text size="sm" c="dimmed">
                When you upload a photo, the AI identifies key facial areas — skin on your cheeks and forehead,
                the whites of your eyes, your hair color, and lip color. It samples hundreds of color points.
              </Text>
            </Paper>
            <Paper withBorder p="lg" radius="md">
              <Title order={3} size="h4" mb="sm">
                2. Undertone Detection
              </Title>
              <Text size="sm" c="dimmed">
                The AI analyzes the ratio of yellow to pink tones in your skin. Warm undertones have more
                yellow pigment, cool undertones have more pink/blue. Neutral falls in between.
              </Text>
            </Paper>
            <Paper withBorder p="lg" radius="md">
              <Title order={3} size="h4" mb="sm">
                3. Contrast Calculation
              </Title>
              <Text size="sm" c="dimmed">
                By measuring the difference between your lightest feature (usually eye whites or skin highlights)
                and darkest feature (usually hair or eyes), the AI determines your contrast level.
              </Text>
            </Paper>
            <Paper withBorder p="lg" radius="md">
              <Title order={3} size="h4" mb="sm">
                4. Seasonal Classification
              </Title>
              <Text size="sm" c="dimmed">
                Your undertone + contrast + depth combination maps to one of 12 seasonal palettes.
                For example: cool undertone + high contrast + medium depth = Cool Winter.
              </Text>
            </Paper>
          </Stack>
        </Card>

        {/* What You Actually Get */}
        <Card withBorder p="xl" radius="md" bg="violet.0">
          <Title order={2} mb="md" ta="center">
            What Your Results Include
          </Title>
          <Text size="md" ta="center" c="dimmed" mb="lg" maw={500} mx="auto">
            Here's a sample of what AI color analysis delivers — your personal palette with specific colors and hex codes.
          </Text>
          <Paper withBorder p="lg" radius="md" bg="white" maw={500} mx="auto">
            <Text fw={600} ta="center" mb="md">Sample: Light Spring Palette</Text>
            <Group justify="center" gap="xs" mb="md">
              {samplePaletteColors.map((hex, i) => (
                <Box key={i} ta="center">
                  <ColorSwatch color={hex} size={40} radius="md" />
                  <Text size="xs" c="dimmed" mt={4}>{hex}</Text>
                </Box>
              ))}
            </Group>
            <Text size="xs" c="dimmed" ta="center">
              Plus 50+ more colors, colors to avoid, makeup recommendations, and outfit ideas
            </Text>
          </Paper>
        </Card>

        {/* Advantages vs Limitations */}
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
          <Card withBorder p="xl" radius="md">
            <Group mb="md">
              <ThemeIcon color="green" variant="light" size="lg">
                <IconCheck size={20} />
              </ThemeIcon>
              <Title order={3} size="h4">
                AI Advantages
              </Title>
            </Group>
            <List size="sm" spacing="sm" icon={<IconCheck size={16} color="green" />}>
              {advantages.map((item, index) => (
                <List.Item key={index}>{item}</List.Item>
              ))}
            </List>
          </Card>

          <Card withBorder p="xl" radius="md">
            <Group mb="md">
              <ThemeIcon color="orange" variant="light" size="lg">
                <IconAlertCircle size={20} />
              </ThemeIcon>
              <Title order={3} size="h4">
                Limitations to Know
              </Title>
            </Group>
            <List size="sm" spacing="sm" icon={<IconX size={16} color="orange" />}>
              {limitations.map((item, index) => (
                <List.Item key={index}>{item}</List.Item>
              ))}
            </List>
          </Card>
        </SimpleGrid>

        {/* Best Results Tips */}
        <Card withBorder p="xl" radius="md" bg="blue.0">
          <Title order={2} mb="lg" ta="center">
            How to Get Accurate AI Results
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            <Paper withBorder p="md" radius="md" bg="white">
              <Text fw={600} mb="xs">Use Natural Light</Text>
              <Text size="sm" c="dimmed">
                Face a window during daytime. Avoid direct sunlight, fluorescent lights, or warm indoor bulbs.
              </Text>
            </Paper>
            <Paper withBorder p="md" radius="md" bg="white">
              <Text fw={600} mb="xs">Skip the Makeup</Text>
              <Text size="sm" c="dimmed">
                Foundation, blush, and color-correcting products change your apparent undertone.
              </Text>
            </Paper>
            <Paper withBorder p="md" radius="md" bg="white">
              <Text fw={600} mb="xs">Wear Neutral Colors</Text>
              <Text size="sm" c="dimmed">
                A white or gray top won't reflect color onto your face and skew the analysis.
              </Text>
            </Paper>
            <Paper withBorder p="md" radius="md" bg="white">
              <Text fw={600} mb="xs">Show Your Natural Hair</Text>
              <Text size="sm" c="dimmed">
                If possible, show your natural hair color. Note if you have dyed or highlighted hair.
              </Text>
            </Paper>
          </SimpleGrid>
        </Card>

        {/* Final CTA */}
        <Card withBorder p="xl" radius="md" bg="violet.0">
          <Stack gap="lg" ta="center">
            <Title order={2} size="h2">
              Ready to Try It?
            </Title>
            <Text size="lg" c="dimmed" maw={500} mx="auto">
              Upload a photo and see AI color analysis in action. Get your season, your best colors,
              and colors to avoid — all in under a minute.
            </Text>
            <Flex gap="md" justify="center" wrap="wrap">
              <Button
                size="lg"
                component={Link}
                href="/ai-color-analysis"
                color="violet"
                rightSection={<IconArrowRight size={20} />}
              >
                Try AI Color Analysis
              </Button>
              <Button
                size="lg"
                variant="outline"
                component={Link}
                href="/color-analysis-quiz"
                color="violet"
              >
                Start With Free Quiz
              </Button>
            </Flex>
            <Text size="sm" c="dimmed">
              Not sure yet? Read about{" "}
              <Text component={Link} href="/how-accurate-is-ai-color-analysis" c="violet" td="underline">
                AI color analysis accuracy
              </Text>{" "}
              or{" "}
              <Text component={Link} href="/ai-vs-in-person-color-analysis" c="violet" td="underline">
                how it compares to in-person consultations
              </Text>
              .
            </Text>
          </Stack>
        </Card>
      </Stack>
    </Container>
  );
}
